<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>
          <v-btn
            color="primary"
            x-small
            text
            aria-label="Voltar"
            @click="$router.push({ name: 'preMatriculaPeriodos' })"
          >
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          {{ editing ? "Editando" : "Abrindo" }} período de pré-matrícula
        </h1>
      </v-col>
    </v-row>

    <v-alert v-if="congelado" type="info" outlined>
      Este período já recebeu pré-matrículas e está bloqueado para edição: ano letivo, datas e
      faixas de desconto não podem mais ser alterados, porque os boletos já foram emitidos com eles.
      Para encerrá-lo, desative-o na listagem de períodos.
    </v-alert>

    <v-alert type="warning" outlined dense>
      A <strong>data de fechamento é o vencimento</strong> de todos os boletos do período. Até a
      data de cada faixa vale o desconto dela; <strong>depois da última faixa</strong>, até o
      fechamento, vale o <strong>valor cheio</strong> da matrícula;
      <strong>depois do fechamento</strong> incidem <strong>multa e juros</strong>.
    </v-alert>

    <ValidationObserver v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(salvar)">
        <e-secao-formulario titulo="Período" icone="fa fa-calendar">
          <v-row>
            <v-col cols="12" sm="4" class="pb-0">
              <ValidationProvider name="Ano letivo" rules="required" v-slot="{ errors }">
                <e-label>Ano letivo</e-label>
                <v-select
                  v-model="form.ano_id"
                  :items="anos"
                  item-text="descricao"
                  item-value="id"
                  :disabled="congelado"
                  :error-messages="errors"
                  dense
                  solo
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="4" class="pb-0">
              <ValidationProvider name="Data de início" rules="required" v-slot="{ errors }">
                <e-label>Data de início</e-label>
                <v-text-field
                  v-model="form.data_inicio"
                  type="date"
                  :disabled="congelado"
                  :error-messages="errors"
                  dense
                  solo
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="4" class="pb-0">
              <ValidationProvider
                name="Data de fechamento"
                :rules="{ required: true, data_nao_anterior: { inicio: form.data_inicio } }"
                v-slot="{ errors }"
              >
                <e-label>Data de fechamento (vencimento dos boletos)</e-label>
                <v-text-field
                  v-model="form.data_fechamento"
                  type="date"
                  :min="form.data_inicio"
                  :disabled="congelado"
                  :error-messages="errors"
                  dense
                  solo
                />
              </ValidationProvider>
            </v-col>
          </v-row>
        </e-secao-formulario>

        <e-secao-formulario
          titulo="Faixas de desconto (opcional, até 3)"
          icone="fa fa-percent"
          subtitulo="Preencha as faixas em ordem. Desconto zero é aceito. O valor de cada faixa é abatido do valor da matrícula da turma."
        >
          <e-faixas-de-desconto
            v-model="form.faixas"
            :inicio="form.data_inicio"
            :fechamento="form.data_fechamento"
            :disabled="congelado"
          />
        </e-secao-formulario>

        <v-btn
          v-if="!congelado"
          color="primary"
          class="mt-2"
          type="submit"
          block
          :loading="salvando"
          :disabled="salvando"
        >
          Salvar período
        </v-btn>
      </form>
    </ValidationObserver>
  </main-template>
</template>

<script>
import { extend } from "vee-validate";
import EFaixasDeDesconto from "@/components/PreMatriculas/EFaixasDeDesconto.vue";
import ESecaoFormulario from "@/components/PreMatriculas/ESecaoFormulario.vue";
import { dataIso, periodoEstaCongelado } from "@/components/PreMatriculas/helpers";

extend("data_nao_anterior", {
  params: ["inicio"],
  validate: (data, { inicio }) => !inicio || data >= inicio,
  message: "A data de fechamento deve ser igual ou posterior à data de início.",
});

export default {
  name: "NewEditPeriodoPage",
  components: { EFaixasDeDesconto, ESecaoFormulario },
  props: {
    editing: { type: Boolean, default: false },
  },
  data() {
    return {
      anos: [],
      congelado: false,
      salvando: false,
      form: { ano_id: null, data_inicio: null, data_fechamento: null, faixas: [] },
    };
  },
  async mounted() {
    this.$loaderService.open("Carregando dados");
    try {
      this.anos = await this.$services.anosService.syncAll();
      if (this.editing) await this.carregarPeriodo();
    } catch (error) {
      this.$handleError(error);
    }
    this.$loaderService.close();
  },
  methods: {
    async carregarPeriodo() {
      const periodo = await this.$services.preMatriculasService.pegarPeriodo(
        this.$route.params.periodo_id
      );
      this.congelado = periodoEstaCongelado(periodo);
      this.form = {
        ano_id: periodo.ano_id,
        data_inicio: dataIso(periodo.data_inicio),
        data_fechamento: dataIso(periodo.data_fechamento),
        faixas: periodo.descontos.map((desconto) => ({
          chave: desconto.id,
          data: dataIso(desconto.data),
          desconto_matriculado: desconto.desconto_matriculado,
          desconto_nao_matriculado: desconto.desconto_nao_matriculado,
        })),
      };
    },
    async salvar() {
      this.salvando = true;
      const service = this.$services.preMatriculasService;
      const dados = {
        ...this.form,
        faixas: this.form.faixas.map(({ chave, ...faixa }) => faixa),
      };
      try {
        if (this.editing) {
          await service.atualizarPeriodo(this.$route.params.periodo_id, dados);
        } else {
          await service.criarPeriodo(dados);
        }
        this.$toast.success("Período salvo.");
        this.$router.push({ name: "preMatriculaPeriodos" });
      } catch (error) {
        this.$handleError(error);
      }
      this.salvando = false;
    },
  },
};
</script>
